import _ from 'lodash';
import { createSelector } from 'reselect';

import {
  get,
  post,
  put,
  del,
} from 'utils/fetch';

import { RequestState, CampaignStatus } from 'constants/constants';
import { listReducer, listWithout } from 'utils/list';

import { selectCurrentAccountId } from 'store/models/Account';
import { selectApiHost } from 'store/AppState';


// ======================
// HELPERS
// ======================
export const getLinkSettings = (campaign, linkId) => _.find(campaign.links, { linkId });
export const isCampaignPaused = campaign => campaign?.status ===  CampaignStatus.PAUSED

// ======================
// ACTIONS
// ====================== 
export const FETCH_CAMPAIGNS = 'FETCH_CAMPAIGNS';
export const FETCH_CAMPAIGN = 'FETCH_CAMPAIGN';
export const UPDATE_CAMPAIGN = 'UPDATE_CAMPAIGN';
export const CREATE_CAMPAIGN = 'CREATE_CAMPAIGN';
export const DELETE_CAMPAIGN = 'DELETE_CAMPAIGN';

export const ADD_CAMPAIGN_CHANNEL = 'ADD_CAMPAIGN_CHANNEL';
export const REMOVE_CAMPAIGN_CHANNEL = 'REMOVE_CAMPAIGN_CHANNEL';
export const UPDATE_CAMPAIGN_CHANNELS = 'UPDATE_CAMPAIGN_CHANNELS';
export const UPDATE_CAMPAIGN_FILTERS = 'UPDATE_CAMPAIGN_FILTERS';

export const ADD_CAMPAIGN_TAG = 'ADD_CAMPAIGN_TAG';
export const REMOVE_CAMPAIGN_TAG = 'REMOVE_CAMPAIGN_TAG';

export const ADD_CAMPAIGN_SCENE = 'ADD_CAMPAIGN_SCENE';
export const UPDATE_CAMPAIGN_SCENE = 'UPDATE_CAMPAIGN_SCENE';
export const REMOVE_CAMPAIGN_SCENE = 'REMOVE_CAMPAIGN_SCENE';
export const MIGRATE_CAMPAIGN_SCENE = 'MIGRATE_CAMPAIGN_SCENE';

export const ADD_CAMPAIGN_LINK = 'ADD_CAMPAIGN_LINK';
export const REMOVE_CAMPAIGN_LINK = 'REMOVE_CAMPAIGN_LINK';
export const UPDATE_CAMPAIGN_LINK = 'UPDATE_CAMPAIGN_LINK';



// ======================
// SELECTORS
// ====================== 
export const selectAllCampaigns = state => state.models.campaigns;
export const selectCampaign = (state, campaignId) => state.models.campaigns[campaignId];
export const selectCampaigns = (state, accountId) => selectCampaignsByAccount(state, accountId ?? selectCurrentAccountId(state))
export const selectCampaignsByAccount = createSelector(
  selectAllCampaigns,
  (state, accountId) =>  accountId,
  (campaigns, accountId) => _.filter(campaigns, { accountId })
)

// ======================
// MODEL
// ====================== 
const Campaign = {
  actions: {
    fetchCampaigns: () => (dispatch, getState) => getState()
      |> selectCurrentAccountId
      |> get({
           url: '/api/account/:accountId/campaigns',
           params: { accountId: # },
           dispatch,
           action: FETCH_CAMPAIGNS,
    }),

    fetchCampaign: campaignId => dispatch => get({
      url: '/api/campaign/:campaignId',
      params: { campaignId },
      dispatch,
      action: FETCH_CAMPAIGN,
    }),

    createCampaign: (values) => dispatch => post({
      url: '/api/campaign',
      body: values,
      dispatch,
      action: CREATE_CAMPAIGN,
    }),

    updateCampaign: (campaignId, values) => dispatch => put({
      url: '/api/campaign/:campaignId',
      params: { campaignId },
      body: values,
      dispatch,
      action: UPDATE_CAMPAIGN,
    }),

    deleteCampaign: campaignId => dispatch => del({
      url: '/api/campaign/:campaignId',
      params: { campaignId },
      dispatch,
      action: DELETE_CAMPAIGN,
    }),

    // CHANNELS
    addChannel: (campaignId, channelId) => dispatch => put({
      url: '/api/campaign/:campaignId/channel/:channelId',
      params: { campaignId, channelId },
      dispatch,
      action: ADD_CAMPAIGN_CHANNEL
    }),

    removeChannel: (campaignId, channelId) => dispatch => del({
      url: '/api/campaign/:campaignId/channel/:channelId',
      params: { campaignId, channelId },
      dispatch,
      action: REMOVE_CAMPAIGN_CHANNEL
    }),

    updateChannels: (campaignId, channelIds) => dispatch => put({
      url: '/api/campaign/:campaignId/channels',
      params: { campaignId },
      body: { channelIds },
      dispatch,
      action: UPDATE_CAMPAIGN_CHANNELS
    }),

    updateFilters: (campaignId, channelIds) => dispatch => put({
      url: '/api/campaign/:campaignId/filtered_channels',
      params: { campaignId },
      body: { channelIds },
      dispatch,
      action: UPDATE_CAMPAIGN_FILTERS
    }),

    // TAGS
    addTag: (campaignId, tagId) => dispatch => put({
      url: '/api/campaign/:campaignId/tag/:tagId',
      params: { campaignId, tagId },
      dispatch,
      action: ADD_CAMPAIGN_TAG
    }),

    removeTag: (campaignId, tagId) => dispatch => del({
      url: '/api/campaign/:campaignId/tag/:tagId',
      params: { campaignId, tagId },
      dispatch,
      action: REMOVE_CAMPAIGN_TAG
    }),

    // SCENES
    addScene: (campaignId, sceneId) => dispatch => put({
      url: '/api/campaign/:campaignId/scene/:sceneId',
      params: { campaignId, sceneId },
      dispatch,
      action: ADD_CAMPAIGN_SCENE
    }),
    
    removeScene: (campaignId, sceneId) => dispatch => del({
      url: '/api/campaign/:campaignId/scene/:sceneId',
      params: { campaignId, sceneId },
      dispatch,
      action: REMOVE_CAMPAIGN_SCENE
    }),

    migrateScene: (sceneId, campaignId) => dispatch => put({
      url: '/api/campaign/:campaignId/migrate_scene/:sceneId',
      params: { sceneId, campaignId },
      dispatch,
      action: MIGRATE_CAMPAIGN_SCENE
    }),

    updateSceneSettings: (campaignId, sceneSettings) => dispatch => put({
      url: '/api/campaign/:campaignId/scene_settings',
      params: { campaignId, },
      body: { sceneSettings },
      dispatch,
      action: UPDATE_CAMPAIGN_SCENE
    }),

    updateSceneOrder: (campaignId, sceneOrder) => dispatch => put({
      url: '/api/campaign/:campaignId/scene_order',
      params: { campaignId, },
      body: { sceneOrder },
      dispatch,
      action: UPDATE_CAMPAIGN_SCENE
    }),

    // LINKS
    addLink: (campaignId, linkId) => dispatch => put({
      url: '/api/campaign/:campaignId/link/:linkId',
      params: { campaignId, linkId },
      dispatch,
      action: ADD_CAMPAIGN_LINK
    }),

    removeLink: (campaignId, linkId) => dispatch => del({
      url: '/api/campaign/:campaignId/link/:linkId',
      params: { campaignId, linkId },
      dispatch,
      action: REMOVE_CAMPAIGN_LINK
    }),

    updateLinkSettings: (campaignId, linkId, settings) => dispatch => post({
      url: '/api/campaign/:campaignId/link/:linkId',
      params: { campaignId, linkId },
      body: { settings },
      dispatch,
      action: UPDATE_CAMPAIGN_LINK
    })


  },
  spec: {
    campaigns: {},
  },
  reducer: (state, type, body, action, json) => {
    if(action.url && action.result !== RequestState.SUCCESS)
      return state;

    switch(type) {
      case DELETE_CAMPAIGN:
        return { ...state, 
          campaigns: _.omit(state.campaigns, body.campaignId),
        }
    }

    if(body && body.campaigns) {
      state = {...state,
        campaigns: {
          ...state.campaigns,
          ..._.keyBy(body.campaigns, '_id'),
        }
      }
    }
    return state;
  },
  listSpec: {
    campaignsByAccount: {},
  },
  listReducer: (state, type, body, action) => {
    if(action.url && action.result !== RequestState.SUCCESS)
      return state;

    switch(type) {
      case DELETE_CAMPAIGN:
        return {
          ...state,
          campaignsByAccount: listWithout(state, 'campaignsByAccount', body.campaignId),
        }
    }

    if(body && body.campaigns) {
      state = {
        ...state,
        campaignsByAccount: listReducer(state, 'campaignsByAccount', body.campaigns, 'accountId', 'campaigns', '_id'),
      }
    }
    return state;
  }
}
export default Campaign;